"use client";

import { useState, useEffect } from "react";
import { useTranslations, useLocale } from "next-intl";
import {
	AreaChart,
	Area,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	Legend,
	ResponsiveContainer,
} from "recharts";
import { getHomeData } from "@/data/getHomeData";

type ChartDataPoint = {
	year: string;
	revenue: number;
	expenses: number;
	netProfit: number;
};
type TooltipPayloadEntry = {
	dataKey: string;
	value?: number;
	color?: string;
	name?: string;
};
type TooltipContentProps = {
	active?: boolean;
	payload?: TooltipPayloadEntry[];
	label?: string;
	revenueLabel: string;
	expensesLabel: string;
	netProfitLabel: string;
};

const SERIES_ORDER = ["revenue", "netProfit", "expenses"] as const;

const LEGEND_ITEMS = [
	{ dataKey: "revenue", color: "#0099ff" },
	{ dataKey: "expenses", color: "#ff0000" },
	{ dataKey: "netProfit", color: "#00aa00" },
] as const;

function CustomLegend({
	revenueLabel,
	expensesLabel,
	netProfitLabel,
}: {
	revenueLabel: string;
	expensesLabel: string;
	netProfitLabel: string;
}) {
	const labels = {
		revenue: revenueLabel,
		expenses: expensesLabel,
		netProfit: netProfitLabel,
	};
	return (
		<div className="flex flex-wrap justify-center gap-6 pt-4">
			{LEGEND_ITEMS.map((item) => (
				<div key={item.dataKey} className="flex items-center gap-2">
					<span
						className="h-5 w-5 shrink-0 rounded-sm"
						style={{ backgroundColor: item.color }}
					/>
					<span className="text-[1.8rem]" style={{ color: "#333" }}>
						{labels[item.dataKey]}
					</span>
				</div>
			))}
		</div>
	);
}

function CustomTooltipContent({
	active,
	payload,
	label,
	revenueLabel,
	expensesLabel,
	netProfitLabel,
}: TooltipContentProps) {
	if (!active || !payload?.length) return null;
	const nameMap: Record<string, string> = {
		revenue: revenueLabel,
		expenses: expensesLabel,
		netProfit: netProfitLabel,
	};
	const sorted = [...payload].sort(
		(a, b) =>
			SERIES_ORDER.indexOf(
				(a.dataKey ?? "revenue") as (typeof SERIES_ORDER)[number],
			) -
			SERIES_ORDER.indexOf(
				(b.dataKey ?? "revenue") as (typeof SERIES_ORDER)[number],
			),
	);
	return (
		<div
			className="rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-md"
			style={{ borderRadius: "8px" }}
		>
			<p className="mb-2 text-[1.6rem] font-medium text-gray-800">{label}</p>
			<ul className="list-none space-y-1">
				{sorted.map((entry) => (
					<li key={entry.dataKey} className="flex items-center gap-2 text-[1.6rem]">
						<span
							className="h-4 w-4 shrink-0 rounded-sm"
							style={{ backgroundColor: entry.color }}
						/>
						<span className="text-gray-700">
							{nameMap[entry.dataKey as string] ?? entry.name}:
						</span>
						<span className="font-medium">
							€{Math.max(0, Number(entry.value)).toLocaleString()}
						</span>
					</li>
				))}
			</ul>
		</div>
	);
}

export default function NumbersSection() {
	const t = useTranslations("Numbers");
	const locale = useLocale() as "en" | "ro";
	const { chartData } = getHomeData(locale);
	const DISPLAY_CHART_DATA = chartData.map((d: ChartDataPoint) => ({
		...d,
		netProfit: Math.max(0, d.netProfit),
	}));
	const [mounted, setMounted] = useState(false);
	useEffect(() => setMounted(true), []);

	return (
		<section className="relative min-h-[75rem] bg-[#ccf5ff] mt-8">
			<div
				className="absolute top-0 left-0 w-full h-[31rem] bg-no-repeat bg-cover"
				style={{ backgroundImage: "url(/assets/vendors/wave-top4.png)" }}
			/>
			<div className="relative w-full section-padding pt-[35rem] pb-20">
				<h3 className="text-[2.8rem] font-normal text-center text-black px-6 py-6">
					{t("title")}
				</h3>
				<div
					className="our-numbers-chart min-h-[400px] w-full p-[10px] flex items-center gap-2"
					style={{ minWidth: 0, boxSizing: "border-box" }}
				>
					{/* Y-axis label: always reserve space to avoid CLS when chart mounts */}
					<div
						className="flex shrink-0 items-center justify-center text-[1.2rem] text-gray-700 font-medium"
						style={{ width: 28, height: 400 }}
					>
						<span
							style={{ transform: "rotate(-90deg)", whiteSpace: "nowrap" }}
						>
							{t("yAxisLabel")}
						</span>
					</div>
					{mounted ? (
						<ResponsiveContainer
							width="100%"
							height={400}
							minWidth={0}
							className="flex-1 min-w-0 box-border"
						>
							<AreaChart
									data={DISPLAY_CHART_DATA}
									margin={{ top: 10, right: 30, left: 10, bottom: 0 }}
								>
									<CartesianGrid strokeDasharray="3 3" />
									<XAxis dataKey="year" />
									<YAxis
										domain={[0, "dataMax"]}
										allowDataOverflow={false}
										tickFormatter={(value) => `€${value.toLocaleString()}`}
									/>
									<Tooltip
										content={
											<CustomTooltipContent
												revenueLabel={t("revenue")}
												expensesLabel={t("expenses")}
												netProfitLabel={t("netProfit")}
											/>
										}
									/>
									<Legend
										content={
											<CustomLegend
												revenueLabel={t("revenue")}
												expensesLabel={t("expenses")}
												netProfitLabel={t("netProfit")}
											/>
										}
									/>
									<defs>
										<linearGradient
											id="areaExpenses"
											x1="0"
											y1="0"
											x2="0"
											y2="1"
										>
											<stop offset="0%" stopColor="#ff0000" stopOpacity={1} />
											<stop
												offset="100%"
												stopColor="#ff0000"
												stopOpacity={0.8}
											/>
										</linearGradient>
										<linearGradient
											id="areaNetProfit"
											x1="0"
											y1="0"
											x2="0"
											y2="1"
										>
											<stop offset="0%" stopColor="#00aa00" stopOpacity={1} />
											<stop
												offset="100%"
												stopColor="#00aa00"
												stopOpacity={0.8}
											/>
										</linearGradient>
										<linearGradient
											id="areaRevenue"
											x1="0"
											y1="0"
											x2="0"
											y2="1"
										>
											<stop offset="0%" stopColor="#0099ff" stopOpacity={0.8} />
											<stop
												offset="100%"
												stopColor="#0099ff"
												stopOpacity={0.4}
											/>
										</linearGradient>
									</defs>
									{/* Draw order: first = back, last = front. Revenue (blue) on top. Red, green, blue via defs. */}
									<Area
										type="monotone"
										dataKey="expenses"
										stroke="#ff0000"
										strokeWidth={2}
										fill="url(#areaExpenses)"
										name={t("expenses")}
									/>
									<Area
										type="monotone"
										dataKey="netProfit"
										stroke="#00aa00"
										strokeWidth={2}
										fill="url(#areaNetProfit)"
										name={t("netProfit")}
									/>
									<Area
										type="monotone"
										dataKey="revenue"
										stroke="#0099ff"
										strokeWidth={2}
										fill="url(#areaRevenue)"
										name={t("revenue")}
									/>
								</AreaChart>
							</ResponsiveContainer>
					) : (
						<div
							className="flex-1 min-w-0 h-[400px] bg-white/50 rounded-lg animate-pulse"
							aria-hidden
						/>
					)}
				</div>
			</div>
		</section>
	);
}
